import { state } from "../utils/lang.ts";
import type { AssessmentResult, FileAttachment } from "../api/types.ts";
import { getKinde, getToken, isAuthenticated, login } from "../utils/auth.ts";

// Module-level storage for the last AI assessment result
let lastAssessmentResult: AssessmentResult | null = null;

// ─── Session-storage draft persistence ────────────────────────────────────────

const DRAFT_KEY = "dammi_assessment_draft";

interface DraftData {
  processName: string;
  description: string;
  /** Checked state for every item checkbox, in DOM order. */
  checkboxes: boolean[];
}

function getItemCheckboxes(): HTMLInputElement[] {
  return Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'details.bg-gradient-to-r tbody tr input[type="checkbox"]',
    ),
  );
}

function saveDraft(): void {
  const processName =
    (document.getElementById("process-name") as HTMLInputElement)?.value ?? "";
  const description =
    (document.getElementById("process-description") as HTMLTextAreaElement)
      ?.value ?? "";
  const checkboxes = getItemCheckboxes().map((cb) => cb.checked);
  const draft: DraftData = { processName, description, checkboxes };
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Ignore storage errors (e.g. private-browsing quota)
  }
}

function restoreDraft(): void {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    const draft = JSON.parse(raw) as DraftData;

    const processNameInput = document.getElementById(
      "process-name",
    ) as HTMLInputElement | null;
    const descriptionInput = document.getElementById(
      "process-description",
    ) as HTMLTextAreaElement | null;

    if (processNameInput && draft.processName) {
      processNameInput.value = draft.processName;
    }
    if (descriptionInput && draft.description) {
      descriptionInput.value = draft.description;
    }

    // Restore item checkboxes and fire change events so section checkboxes update
    const checkboxes = getItemCheckboxes();
    (draft.checkboxes ?? []).forEach((checked, i) => {
      const cb = checkboxes[i];
      if (cb && cb.checked !== checked) {
        cb.checked = checked;
        cb.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  } catch {
    // Ignore parse errors
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Wire up live-saving of form inputs to sessionStorage and restore any
 * previously saved draft. Must be called after initChecklistAutomation so
 * that section-checkbox change listeners are already in place.
 */
export function initFormDraftPersistence(): void {
  restoreDraft();

  document.getElementById("process-name")?.addEventListener(
    "input",
    saveDraft,
  );
  document.getElementById("process-description")?.addEventListener(
    "input",
    saveDraft,
  );

  // Delegate checkbox changes (item checkboxes inside the checklist)
  document.addEventListener("change", (e) => {
    const target = e.target as HTMLElement;
    if (
      target.matches(
        'details.bg-gradient-to-r tbody tr input[type="checkbox"]',
      )
    ) {
      saveDraft();
    }
  });
}

export function getLastAssessmentResult(): AssessmentResult | null {
  return lastAssessmentResult;
}

// Read a File object and return it as a base64 string (no data-URL prefix)
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the "data:<mime>;base64," prefix
      const base64 = dataUrl.split(",")[1] ?? "";
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Collect files selected in the upload input and convert them to FileAttachment objects
async function collectAttachments(): Promise<FileAttachment[]> {
  const input = document.getElementById(
    "file-upload-input",
  ) as HTMLInputElement | null;
  if (!input?.files?.length) return [];
  const attachments: FileAttachment[] = [];
  for (const file of Array.from(input.files)) {
    const data = await readFileAsBase64(file);
    attachments.push({ data, mimeType: file.type, name: file.name });
  }
  return attachments;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_FILE_COUNT = 2;
const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const fileErrorMessages: Record<string, Record<string, string>> = {
  tooMany: {
    en: `You can attach at most ${MAX_FILE_COUNT} files.`,
    lv: `Varat pievienot ne vairāk kā ${MAX_FILE_COUNT} failus.`,
    it: `Puoi allegare al massimo ${MAX_FILE_COUNT} file.`,
  },
  tooLarge: {
    en: "One or more files exceed the 2 MB limit.",
    lv: "Viens vai vairāki faili pārsniedz 2 MB ierobežojumu.",
    it: "Uno o più file superano il limite di 2 MB.",
  },
  badType: {
    en: "Only PDF, JPEG, PNG, WEBP, and GIF files are allowed.",
    lv: "Atļauti tikai PDF, JPEG, PNG, WEBP un GIF faili.",
    it: "Sono consentiti solo file PDF, JPEG, PNG, WEBP e GIF.",
  },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Render the file list UI and wire up remove buttons
function renderFileList(files: File[]): void {
  const list = document.getElementById("file-list");
  if (!list) return;
  list.innerHTML = files
    .map(
      (f, i) => `
    <li class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-100 text-sm text-slate-700" data-index="${i}">
      <span class="truncate flex-1">${f.name}</span>
      <span class="text-slate-400 shrink-0">${formatFileSize(f.size)}</span>
      <button type="button" data-remove="${i}" class="shrink-0 text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove file">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </li>`,
    )
    .join("");
}

// Validate and apply a FileList to the hidden input, updating the UI
function applyFiles(
  incoming: FileList | File[],
  input: HTMLInputElement,
): void {
  const errorEl = document.getElementById("file-error");
  const lang = state.lang;

  // Merge with existing files
  const existing = input.files ? Array.from(input.files) : [];
  const combined = [...existing, ...Array.from(incoming)];

  // Deduplicate by name+size
  const seen = new Set<string>();
  const merged = combined.filter((f) => {
    const key = `${f.name}:${f.size}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Validate
  const badType = merged.some((f) => !ACCEPTED_TYPES.has(f.type));
  if (badType) {
    if (errorEl) {
      errorEl.textContent = fileErrorMessages.badType[lang] ??
        fileErrorMessages.badType["en"];
      errorEl.classList.remove("hidden");
    }
    return;
  }
  if (merged.length > MAX_FILE_COUNT) {
    if (errorEl) {
      errorEl.textContent = fileErrorMessages.tooMany[lang] ??
        fileErrorMessages.tooMany["en"];
      errorEl.classList.remove("hidden");
    }
    return;
  }
  const tooLarge = merged.some((f) => f.size > MAX_FILE_SIZE);
  if (tooLarge) {
    if (errorEl) {
      errorEl.textContent = fileErrorMessages.tooLarge[lang] ??
        fileErrorMessages.tooLarge["en"];
      errorEl.classList.remove("hidden");
    }
    return;
  }

  if (errorEl) errorEl.classList.add("hidden");

  // Assign to the input via a new DataTransfer
  const dt = new DataTransfer();
  merged.forEach((f) => dt.items.add(f));
  input.files = dt.files;

  renderFileList(merged);
}

// Initialize the file upload drop zone and list interactions
export function initFileUpload(): void {
  const input = document.getElementById(
    "file-upload-input",
  ) as HTMLInputElement | null;
  const dropZone = document.getElementById("file-drop-zone");
  const list = document.getElementById("file-list");

  if (!input || !dropZone) return;

  // File input change
  input.addEventListener("change", () => {
    if (input.files?.length) {
      const files = Array.from(input.files);
      // Reset input so applyFiles can do a clean merge from scratch
      input.files = new DataTransfer().files;
      applyFiles(files, input);
    }
  });

  // Keyboard activation of drop zone
  dropZone.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      input.click();
    }
  });

  // Drag events
  dropZone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropZone.classList.add("border-indigo-500", "bg-indigo-50");
  });
  dropZone.addEventListener("dragleave", () => {
    dropZone.classList.remove("border-indigo-500", "bg-indigo-50");
  });
  dropZone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropZone.classList.remove("border-indigo-500", "bg-indigo-50");
    if (e.dataTransfer?.files.length) {
      applyFiles(e.dataTransfer.files, input);
    }
  });

  // Remove individual files
  list?.addEventListener("click", (e) => {
    const btn = (e.target as Element).closest("[data-remove]") as
      | HTMLElement
      | null;
    if (!btn) return;
    const idx = parseInt(btn.dataset.remove ?? "-1", 10);
    if (idx < 0) return;
    const files = input.files ? Array.from(input.files) : [];
    files.splice(idx, 1);
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    input.files = dt.files;
    renderFileList(files);
  });
}

// Tick checklist checkboxes based on AI assessment result
export function applyCheckedItems(
  checkedItems: {
    sectionIndex: number;
    subsectionIndex: number;
    itemIndex: number;
  }[],
): void {
  const sections = document.querySelectorAll("details.bg-gradient-to-r");

  for (const ref of checkedItems) {
    const section = sections[ref.sectionIndex];
    if (!section) continue;

    const subsections = section.querySelectorAll("div.mb-6");
    const subsection = subsections[ref.subsectionIndex];
    if (!subsection) continue;

    const rows = subsection.querySelectorAll("tbody tr");
    const row = rows[ref.itemIndex];
    if (!row) continue;

    const checkbox = row.querySelector(
      'input[type="checkbox"]',
    ) as HTMLInputElement | null;
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      // Expand the parent details section
      (section as HTMLDetailsElement).open = true;
    }
  }
}

// Initialize AI Assessment functionality
export function initAIAssessment(): void {
  const aiBtn = document.getElementById("ai-assessment-btn");
  const modal = document.getElementById(
    "ai-assessment-modal",
  ) as HTMLDialogElement;
  const loadingEl = document.getElementById("ai-assessment-loading");
  const errorEl = document.getElementById("ai-assessment-error");
  const cancelBtn = document.getElementById("ai-assessment-cancel-btn");

  // Login-required interstitial modal
  const loginModal = document.getElementById(
    "login-required-modal",
  ) as HTMLDialogElement | null;
  const loginModalCancelBtn = document.getElementById("login-modal-cancel-btn");
  const loginModalLoginBtn = document.getElementById("login-modal-login-btn");

  loginModalCancelBtn?.addEventListener("click", () => loginModal?.close());
  loginModalLoginBtn?.addEventListener("click", () => login());

  if (!aiBtn || !modal) {
    console.warn("AI Assessment button or modal not found. Skipping.");
    return;
  }

  let abortController: AbortController | null = null;
  let fetchInProgress = false;

  const confirmMessages: Record<string, string> = {
    en: "Are you sure you want to cancel the AI assessment?",
    lv: "Vai tiešām vēlaties atcelt AI novērtējumu?",
    it: "Sei sicuro di voler annullare la valutazione AI?",
  };

  function handleCancel() {
    if (fetchInProgress) {
      const msg = confirmMessages[state.lang] ?? confirmMessages["en"];
      if (!confirm(msg)) return;
      abortController?.abort();
    }
    modal.close();
  }

  // Prevent closing via Escape key — route through cancel confirmation instead
  modal.addEventListener("cancel", (e) => {
    e.preventDefault();
    handleCancel();
  });

  cancelBtn?.addEventListener("click", handleCancel);

  aiBtn.addEventListener("click", async () => {
    const processNameInput = document.getElementById(
      "process-name",
    ) as HTMLInputElement;
    const processDescriptionInput = document.getElementById(
      "process-description",
    ) as HTMLTextAreaElement;

    if (!processNameInput?.value?.trim()) {
      processNameInput?.focus();
      processNameInput?.classList.add(
        "ring-2",
        "ring-red-500",
        "border-red-500",
      );
      setTimeout(() => {
        processNameInput?.classList.remove(
          "ring-2",
          "ring-red-500",
          "border-red-500",
        );
      }, 2000);
      return;
    }

    if (!processDescriptionInput?.value?.trim()) {
      processDescriptionInput?.focus();
      processDescriptionInput?.classList.add(
        "ring-2",
        "ring-red-500",
        "border-red-500",
      );
      setTimeout(() => {
        processDescriptionInput?.classList.remove(
          "ring-2",
          "ring-red-500",
          "border-red-500",
        );
      }, 2000);
      return;
    }

    // ToS / Privacy Policy consent check
    const tosCheckbox = document.getElementById(
      "tos-consent-checkbox",
    ) as HTMLInputElement | null;
    const tosError = document.getElementById("tos-consent-error");
    if (tosCheckbox && !tosCheckbox.checked) {
      tosError?.classList.remove("hidden");
      tosCheckbox.focus();
      tosCheckbox.closest("label")?.classList.add("text-red-600");
      setTimeout(() => {
        tosError?.classList.add("hidden");
        tosCheckbox.closest("label")?.classList.remove("text-red-600");
      }, 4000);
      return;
    }

    // Auth check — show login interstitial if Kinde is configured but user is not logged in
    if (getKinde() !== null && !await isAuthenticated()) {
      loginModal?.showModal();
      return;
    }

    // Reset modal state and open
    if (loadingEl) loadingEl.style.display = "flex";
    if (errorEl) errorEl.style.display = "none";

    modal.showModal();

    aiBtn.classList.add("opacity-50", "cursor-wait");
    (aiBtn as HTMLButtonElement).disabled = true;

    abortController = new AbortController();
    fetchInProgress = true;

    try {
      const data = collectAssessmentData();
      const attachments = await collectAttachments();
      if (attachments.length > 0) {
        data.attachments = attachments;
      }
      const token = await getToken();
      const response = await fetch("/api/generate-assessment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();

      // Store result for use in PDF export
      lastAssessmentResult = result as AssessmentResult;

      if (result.checkedItems?.length) {
        applyCheckedItems(result.checkedItems);
      }

      // Render commentary box on the page, before the checklist
      renderCommentaryBox(result as AssessmentResult);

      // Hide the button after a successful assessment and close the modal
      aiBtn.style.display = "none";
      modal.close();
      clearDraft();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // User cancelled — silently close, no error shown
      } else {
        console.error("AI assessment error:", error);
        if (errorEl) errorEl.style.display = "block";
      }
    } finally {
      fetchInProgress = false;
      abortController = null;
      if (loadingEl) loadingEl.style.display = "none";
      aiBtn.classList.remove("opacity-50", "cursor-wait");
      (aiBtn as HTMLButtonElement).disabled = false;
    }
  });
}

// Collect assessment data from the checklist
export function collectAssessmentData(): {
  processName: string;
  description: string;
  sections: {
    title: string;
    checked: boolean;
    subsections: {
      title: string;
      items: { text: string; checked: boolean }[];
    }[];
  }[];
  lang: string;
  attachments?: FileAttachment[];
} {
  const processName =
    (document.getElementById("process-name") as HTMLInputElement)?.value || "";
  const description =
    (document.getElementById("process-description") as HTMLTextAreaElement)
      ?.value || "";

  const sections: {
    title: string;
    checked: boolean;
    subsections: {
      title: string;
      items: { text: string; checked: boolean }[];
    }[];
  }[] = [];

  // Get all phase sections (details elements)
  const phases = document.querySelectorAll("details.bg-gradient-to-r");

  phases.forEach((phase) => {
    const phaseElement = phase as HTMLElement;
    const summary = phaseElement.querySelector("summary");
    const sectionTitle =
      summary?.querySelector("span[data-en]")?.textContent?.trim() || "";
    const sectionCheckbox = summary?.querySelector(
      'input[type="checkbox"]',
    ) as HTMLInputElement;

    const subsections: {
      title: string;
      items: { text: string; checked: boolean }[];
    }[] = [];

    // Get all subsection containers
    const subsectionElements = phaseElement.querySelectorAll("div.mb-6");

    subsectionElements.forEach((subsection) => {
      const subsectionTitle =
        subsection.querySelector("h4 span[data-en]")?.textContent?.trim() || "";
      const items: { text: string; checked: boolean }[] = [];

      const itemRows = subsection.querySelectorAll("tbody tr");
      itemRows.forEach((row) => {
        const checkbox = row.querySelector(
          'input[type="checkbox"]',
        ) as HTMLInputElement;
        const text = row.querySelector("span[data-en]")?.textContent?.trim() ||
          "";
        if (text) {
          items.push({
            text,
            checked: checkbox?.checked || false,
          });
        }
      });

      if (subsectionTitle && items.length > 0) {
        subsections.push({
          title: subsectionTitle,
          items,
        });
      }
    });

    if (sectionTitle) {
      sections.push({
        title: sectionTitle,
        checked: sectionCheckbox?.checked || false,
        subsections,
      });
    }
  });

  return {
    processName,
    description,
    sections,
    lang: state.lang,
  };
}

// Render AI commentary into the on-page commentary box (shown before the checklist)
export function renderCommentaryBox(result: AssessmentResult): void {
  const box = document.getElementById("ai-commentary-box");
  if (!box) return;

  const categories = [
    {
      key: "standardization" as const,
      label: "Standardization",
      color: "#10b981",
      bg: "#d1fae5",
    },
    {
      key: "digitalization" as const,
      label: "Digitalization",
      color: "#3b82f6",
      bg: "#dbeafe",
    },
    {
      key: "automation" as const,
      label: "Automation",
      color: "#8b5cf6",
      bg: "#ede9fe",
    },
    { key: "ai" as const, label: "AI", color: "#f59e0b", bg: "#fef3c7" },
  ];

  const scoreCards = categories.map((cat) => {
    const score = Math.round(result.scores[cat.key] ?? 0);
    return `
      <div style="background:${cat.bg};border-radius:10px;padding:12px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
          <span style="font-weight:600;color:#1e293b;font-size:0.875rem;">${cat.label}</span>
          <span style="font-weight:700;color:${cat.color};font-size:1rem;">${score}%</span>
        </div>
        <div style="background:#e2e8f0;border-radius:9999px;height:6px;overflow:hidden;">
          <div style="background:${cat.color};width:${score}%;height:100%;border-radius:9999px;"></div>
        </div>
      </div>`;
  }).join("");

  const recommendationCards = categories.map((cat) => {
    const rec = result.recommendations[cat.key] ?? "";
    return `
      <div style="border-left:3px solid ${cat.color};padding:10px 14px;background:#f8fafc;border-radius:0 8px 8px 0;margin-bottom:10px;">
        <div style="font-weight:600;color:#1e293b;font-size:0.875rem;margin-bottom:3px;">${cat.label}</div>
        <div style="color:#475569;font-size:0.875rem;line-height:1.5;">${rec}</div>
      </div>`;
  }).join("");

  box.innerHTML = `
    <div style="border:1px solid #ddd6fe;border-radius:14px;padding:20px;background:#faf5ff;" class="mb-6">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
        <svg width="18" height="18" fill="none" stroke="#7c3aed" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 3l1.5 1.5M19 3l-1.5 1.5M12 2v1M3 12h1m16 0h1M5 21l1.5-1.5M19 21l-1.5-1.5M9 12a3 3 0 106 0 3 3 0 00-6 0z"/>
        </svg>
        <span style="font-weight:700;color:#5b21b6;font-size:1rem;">AI Assessment Summary</span>
      </div>
      <p style="color:#5b21b6;font-size:0.9375rem;line-height:1.6;margin:0 0 16px;padding:12px;background:#ede9fe;border-radius:8px;">${result.overallSummary}</p>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;">
        ${scoreCards}
      </div>
      <div>${recommendationCards}</div>
    </div>
    <div style="display:flex;align-items:center;gap:10px;padding:12px 16px;background:#fffbeb;border:1px solid #fde68a;border-radius:10px;margin-bottom:16px;">
      <svg width="18" height="18" fill="none" stroke="#d97706" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0;">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      </svg>
      <span style="color:#92400e;font-size:0.875rem;line-height:1.5;"
        data-en="Please review the AI-checked items below — AI may make mistakes."
        data-lv="Lūdzu, pārskatiet zemāk atzīmētos MI vienums — MI var kļūdīties."
        data-it="Si prega di esaminare gli elementi selezionati dall'IA qui sotto — l'IA può commettere errori."
      >Please review the AI-checked items below — AI may make mistakes.</span>
    </div>
  `;
  box.style.display = "block";
}
